import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { usersApi } from "../api/usersApi";
import { pointsApi } from "../api/pointsApi";
import { ApiError } from "../api/apiError";
import type {
  PointsTransaction,
} from "../types/points.types";
import type {
  UserAddress,
  UserRecord,
  UpdateUserInput,
  UserWorkImage,
} from "../types/users.types";
import { GoogleMap, Marker, } from "@react-google-maps/api";
import "../screenstyles/EditAccount.css";

import GoogleMapLoader from "../components/GoogleMapLoader";

import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";


const isUserWorkImage = (
  value: unknown,
): value is UserWorkImage => {
  return (
    typeof value === "object" &&
    value !== null &&
    "imageId" in value &&
    "imageUrl" in value
  );
};


interface EditAccountData
  extends UserRecord {
  about: string;
  dateOfBirth: string;
  gender: string;
  rating: string;
}
const mapUserToEditAccount = (
  user: UserRecord,
): EditAccountData => {
  const raw =
    user.raw &&
    typeof user.raw === "object"
      ? user.raw
      : {};

  return {
    ...user,

    id:
      user.id ||
      user.uid,

    uid:
      user.uid ||
      user.id,

    fullName:
      user.fullName || "",

    email:
      user.email || "",

    phoneNumber:
      user.phoneNumber || "",

    profileImageUrl:
      user.profileImageUrl || "",

    accountType:
      user.accountType || "مستخدم",

    province:
      user.province || "",

    points:
      Number(user.points || 0),

    about:
      typeof raw.about === "string"
        ? raw.about
        : "",

    dateOfBirth:
      typeof raw.dateOfBirth === "string"
        ? raw.dateOfBirth
        : "",

    gender:
      typeof raw.gender === "string"
        ? raw.gender
        : "",

    rating:
      raw.rating !== undefined &&
      raw.rating !== null
        ? String(raw.rating)
        : "",
  };
};

const mapAddressesToLocations = (
  addresses: UserAddress[],
): {
  locationName: string;
  City: string;
  lat: number;
  lng: number;
}[] => {
  return addresses
    .map((address) => {
      const latitude =
        address.location?.latitude;

      const longitude =
        address.location?.longitude;

      if (
        typeof latitude !== "number" ||
        typeof longitude !== "number"
      ) {
        return null;
      }

      return {
        locationName:
          address.locationName ||
          address.addressName ||
          "",

        City:
          address.City || "",

        lat: latitude,
        lng: longitude,
      };
    })
    .filter(
      (
        location,
      ): location is {
        locationName: string;
        City: string;
        lat: number;
        lng: number;
      } => location !== null,
    );
};

const getTransactionSerial = (
  transaction: PointsTransaction,
): string | null => {
  if (transaction.sourceId) {
    return transaction.sourceId;
  }

  if (
    transaction.transactionId &&
    /^FN/i.test(
      transaction.transactionId,
    )
  ) {
    return transaction.transactionId;
  }

  if (
    transaction.note
  ) {
    const match =
      transaction.note.match(
        /FN[0-9A-Z_-]+/i,
      );

    if (match) {
      return match[0];
    }
  }

  return null;
};


const EditAccount: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"images" | "locations" | "Points">(
    "images"
  );

  const [operationType, setOperationType] = useState("add");
  const [newPoints, setNewPoints] = useState("");
  const [note, setNote] = useState("");

  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [showImagePreviewModal, setShowImagePreviewModal] = useState(false);

const [Account, setAccount] =
  useState<EditAccountData | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [
  accountLoading,
  setAccountLoading,
] = useState(true);

const [
  accountLoadError,
  setAccountLoadError,
] = useState<string | null>(null);

  const storage = getStorage();

  const [mainImage, setMainImage] = useState<File | null>(null);
  const [mainImageUrl, setMainImageUrl] = useState<string | null>(null);
  const [extraImages, setExtraImages] = useState<File[]>([]);
const [
  storedExtraImages,
  setStoredExtraImages,
] = useState<UserWorkImage[]>([]);


  const [userLocations, setUserLocations] = useState<
    { locationName: string; City: string; lat: number; lng: number }[]
  >([]);
const [
  userPointsData,
  setUserPointsData,
] = useState<PointsTransaction[]>([]);

const [
  pointsLoading,
  setPointsLoading,
] = useState(false);

const [
  pointsActionLoading,
  setPointsActionLoading,
] = useState(false);

const [
  pointsHasMore,
  setPointsHasMore,
] = useState(false);

const [
  pointsNextCursor,
  setPointsNextCursor,
] = useState<string | null>(null);

 
const [
  imageToDelete,
  setImageToDelete,
] = useState<
  | UserWorkImage
  | string
  | File
  | null
>(null);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);


  const mapContainerStyle = {
    width: "100%",
    height: "200px",
    borderRadius: "12px",
  };






const fetchAccount = async () => {
  if (!id) {
    setAccountLoadError(
      "معرف المستخدم غير موجود",
    );

    setAccountLoading(false);
    return;
  }

  try {
    setAccountLoading(true);
    setAccountLoadError(null);

    const result =
      await usersApi.getById(id);

    const mappedAccount =
      mapUserToEditAccount(
        result.user,
      );

    setAccount(mappedAccount);

    setMainImageUrl(
      mappedAccount.profileImageUrl ||
        null,
    );


    /*
     * الصور الإضافية ما زالت حاليًا
     * مرتبطة مباشرة بـ Firestore.
     */
 
try {
  const imagesResult =
    await usersApi.getImages(id);

  setStoredExtraImages(
    imagesResult.images,
  );
} catch (error) {
  console.error(
    "Error loading user images:",
    error,
  );

  setStoredExtraImages([]);
}
 
   
   
const addressesResult =
  await usersApi.getAddresses(
    id,
  );

const locations =
  mapAddressesToLocations(
    addressesResult.items,
  );

setUserLocations(
  locations,
);

  } catch (error) {
    console.error(
      "Error loading user account:",
      error,
    );

    setAccount(null);

    if (error instanceof ApiError) {
      if (error.status === 401) {
        setAccountLoadError(
          "انتهت جلسة تسجيل الدخول، يرجى تسجيل الدخول مجددًا",
        );

        return;
      }

      if (error.status === 403) {
        setAccountLoadError(
          "لا تمتلك صلاحية عرض بيانات المستخدم",
        );

        return;
      }

      if (error.status === 404) {
        setAccountLoadError(
          "لم يتم العثور على المستخدم",
        );

        return;
      }

      setAccountLoadError(
        error.message ||
          "حدث خطأ أثناء تحميل بيانات المستخدم",
      );

      return;
    }

    setAccountLoadError(
      "تعذر الاتصال بالخادم أثناء تحميل بيانات المستخدم",
    );
  } finally {
    setAccountLoading(false);
  }
};

const fetchUserPoints = async (
  options: {
    append?: boolean;
    cursor?: string;
  } = {},
) => {
  if (!id) {
    return;
  }

  const {
    append = false,
    cursor,
  } = options;

  try {
    setPointsLoading(true);

    const result =
      await pointsApi.getUserTransactions(
        id,
        {
          limit: 20,
          cursor,
        },
      );

    if (append) {
      setUserPointsData(
        (previous) => {
         const transactionMap =
  new Map<
    string,
    PointsTransaction
  >();

previous.forEach(
  (transaction) => {
    transactionMap.set(
      transaction.id,
      transaction,
    );
  },
);

result.items.forEach(
  (transaction) => {
    transactionMap.set(
      transaction.id,
      transaction,
    );
  },
);

          return Array.from(
            transactionMap.values(),
          );
        },
      );
    } else {
      setUserPointsData(
        result.items,
      );
    }

    setPointsHasMore(
      result.hasMore,
    );

    setPointsNextCursor(
      result.nextCursor,
    );
  } catch (error) {
    console.error(
      "Error loading points transactions:",
      error,
    );

    if (error instanceof ApiError) {
      if (error.status === 403) {
        toast.error(
          "لا تمتلك صلاحية عرض سجل النقاط",
        );

        return;
      }

      toast.error(
        error.message ||
          "حدث خطأ أثناء جلب سجل النقاط",
      );

      return;
    }

    toast.error(
      "تعذر الاتصال بالخادم أثناء جلب النقاط",
    );
  } finally {
    setPointsLoading(false);
  }
};


useEffect(() => {
  fetchAccount();
  fetchUserPoints();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [id]);



const handleAddPoints = async () => {
  if (!id) {
    return;
  }

  const parsedPoints =
    Number(newPoints);

  if (
    !Number.isInteger(
      parsedPoints,
    ) ||
    parsedPoints <= 0
  ) {
    toast.error(
      "يرجى إدخال عدد نقاط صحيح أكبر من صفر",
    );

    return;
  }

  if (!note.trim()) {
    toast.error(
      "يرجى كتابة سبب العملية",
    );

    return;
  }

  const actualPoints =
    operationType === "withdraw"
      ? -Math.abs(parsedPoints)
      : Math.abs(parsedPoints);

  try {
    setPointsActionLoading(true);

    const result =
      await pointsApi.adjustUserPoints(
        id,
        {
          points:
            actualPoints,

          reason:
            note.trim(),

          source:
            "dashboard",

          allowNegativeBalance:
            false,
        },
      );

    setAccount(
      (previous) => {
        if (!previous) {
          return previous;
        }

        return {
          ...previous,
          points:
            result.balanceAfter,
        };
      },
    );

    setNewPoints("");
    setNote("");

    toast.success(
      actualPoints > 0
        ? `تمت إضافة ${actualPoints} نقطة بنجاح`
        : `تم سحب ${Math.abs(
            actualPoints,
          )} نقطة بنجاح`,
    );

    await fetchUserPoints();
  } catch (error) {
    console.error(
      "Error adjusting points:",
      error,
    );

    if (error instanceof ApiError) {
      if (error.status === 403) {
        toast.error(
          "لا تمتلك صلاحية تعديل نقاط المستخدم",
        );

        return;
      }

      if (error.status === 404) {
        toast.error(
          "لم يتم العثور على المستخدم",
        );

        return;
      }

      if (error.status === 409) {
        toast.error(
          "رصيد المستخدم غير كافٍ لتنفيذ عملية السحب",
        );

        return;
      }

      if (error.status === 400) {
        toast.error(
          error.message ||
            "بيانات عملية النقاط غير صحيحة",
        );

        return;
      }

      toast.error(
        error.message ||
          "حدث خطأ أثناء تعديل النقاط",
      );

      return;
    }

    toast.error(
      "تعذر الاتصال بالخادم أثناء تعديل النقاط",
    );
  } finally {
    setPointsActionLoading(false);
  }
};


const handleReversePoint = async (
  transaction: PointsTransaction,
) => {
  if (
    !id ||
    pointsActionLoading
  ) {
    return;
  }

  if (transaction.points === 0) {
    return;
  }

  const reversePoints =
    -transaction.points;

  const confirmed =
    window.confirm(
      `هل تريد عكس العملية (${transaction.points} نقطة)؟`,
    );

  if (!confirmed) {
    return;
  }

  try {
    setPointsActionLoading(true);

    const result =
      await pointsApi.adjustUserPoints(
        id,
        {
          points:
            reversePoints,

          reason:
            `عكس العملية ${transaction.transactionId}`,

          source:
            "dashboard_reverse",

          allowNegativeBalance:
            false,
        },
      );

    setAccount(
      (previous) => {
        if (!previous) {
          return previous;
        }

        return {
          ...previous,
          points:
            result.balanceAfter,
        };
      },
    );

    toast.success(
      "تم عكس عملية النقاط بنجاح",
    );

    await fetchUserPoints();
  } catch (error) {
    console.error(
      "Error reversing points transaction:",
      error,
    );

    if (error instanceof ApiError) {
      if (error.status === 409) {
        toast.error(
          "لا يمكن عكس العملية لأن الرصيد سيصبح سالبًا",
        );

        return;
      }

      toast.error(
        error.message ||
          "تعذر عكس عملية النقاط",
      );

      return;
    }

    toast.error(
      "حدث خطأ أثناء عكس العملية",
    );
  } finally {
    setPointsActionLoading(false);
  }
};

const handleSubmit = async (
  event: React.FormEvent,
) => {
  event.preventDefault();

  if (!id || !Account) {
    toast.error(
      "لا توجد بيانات مستخدم للحفظ",
    );

    return;
  }

  const fullName =
    Account.fullName.trim();

  const email =
    Account.email?.trim()
      .toLowerCase() || "";

  const phoneNumber =
    Account.phoneNumber?.trim() ||
    "";

  if (fullName.length < 2) {
    toast.error(
      "يرجى إدخال اسم صحيح",
      {
        duration: 3000,
        position: "top-right",
      },
    );

    return;
  }

  if (!email) {
    toast.error(
      "يرجى إدخال البريد الإلكتروني",
      {
        duration: 3000,
        position: "top-right",
      },
    );

    return;
  }

  const emailPattern =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailPattern.test(email)) {
    toast.error(
      "صيغة البريد الإلكتروني غير صحيحة",
      {
        duration: 3000,
        position: "top-right",
      },
    );

    return;
  }

  try {
    setLoading(true);

    const updateInput:
      UpdateUserInput = {
        fullName,
        email,

        phoneNumber:
          phoneNumber || null,

        profileImageUrl:
          Account.profileImageUrl ||
          null,

        province:
          Account.province ||
          null,

        language:
          Account.language ||
          "ar",

        notificationsEnabled:
          Account.notificationsEnabled,
      };

    const result =
      await usersApi.update(
        id,
        updateInput,
      );

    const updatedAccount =
      mapUserToEditAccount(
        result.user,
      );

    /*
     * نحافظ مؤقتًا على الحقول القديمة
     * التي لم يرجعها الـ API أو لا يدعم
     * تعديلها حتى الآن.
     */
    setAccount((previous) => {
      if (!previous) {
        return updatedAccount;
      }

      return {
        ...previous,
        ...updatedAccount,

        about:
          previous.about,

        dateOfBirth:
          previous.dateOfBirth,

        gender:
          previous.gender,

        rating:
          previous.rating,
      };
    });

    toast.success(
      "تم تحديث بيانات المستخدم الأساسية بنجاح",
      {
        duration: 3000,
        position: "top-right",
        style: {
          background:
            "linear-gradient(to top, rgb(25, 113, 132), rgb(38, 155, 180))",

          color: "#fff",
          fontSize: "16px",
        },
      },
    );
  } catch (error) {
    console.error(
      "Error updating user:",
      error,
    );

    if (error instanceof ApiError) {
      if (error.status === 400) {
        toast.error(
          error.message ||
            "بيانات المستخدم غير صحيحة",
          {
            duration: 4000,
            position: "top-right",
          },
        );

        return;
      }

      if (error.status === 403) {
        toast.error(
          "لا تمتلك صلاحية تعديل المستخدم",
          {
            duration: 4000,
            position: "top-right",
          },
        );

        return;
      }

      if (error.status === 404) {
        toast.error(
          "لم يتم العثور على المستخدم",
          {
            duration: 4000,
            position: "top-right",
          },
        );

        return;
      }

      if (error.status === 409) {
        toast.error(
          error.message ||
            "البريد الإلكتروني أو رقم الهاتف مستخدم مسبقًا",
          {
            duration: 4000,
            position: "top-right",
          },
        );

        return;
      }

      toast.error(
        error.message ||
          "حدث خطأ أثناء تحديث المستخدم",
        {
          duration: 4000,
          position: "top-right",
        },
      );

      return;
    }

    toast.error(
      "تعذر الاتصال بالخادم أثناء تحديث المستخدم",
      {
        duration: 4000,
        position: "top-right",
      },
    );
  } finally {
    setLoading(false);
  }
};


  const handleUpdateMainImage = async () => {
  if (!id) {
    toast.error("لا يوجد معرف للمستخدم");
    return;
  }

  if (!mainImage) {
    toast.error("يرجى اختيار صورة جديدة");
    return;
  }

  try {
    setLoading(true);

    const safeFileName = `${Date.now()}-${mainImage.name}`;
    const mainImageRef = ref(
      storage,
      `users/${id}/profileImage/${safeFileName}`
    );

    await uploadBytes(mainImageRef, mainImage);

    const uploadedImageUrl = await getDownloadURL(mainImageRef);

   await usersApi.update(
  id,
  {
    profileImageUrl:
      uploadedImageUrl,
  },
);

    setMainImageUrl(uploadedImageUrl);
    setMainImage(null);

    setAccount((prev: any) => ({
      ...prev,
      profileImageUrl: uploadedImageUrl,
    }));

    toast.success("✅ تم تحديث صورة المستخدم بنجاح", {
      duration: 3000,
      position: "top-right",
      style: {
        background:
          "linear-gradient(to top, rgb(25, 113, 132), rgb(38, 155, 180))",
        color: "#fff",
        fontSize: "16px",
      },
    });

    fetchAccount();
  } catch (error) {
    console.error("❌ خطأ أثناء تحديث صورة المستخدم:", error);
    toast.error("حدث خطأ أثناء تحديث الصورة");
  } finally {
    setLoading(false);
  }
};
if (accountLoading) {
  return (
    <div
      className="edit-account-page"
      dir="rtl"
    >
      <div className="edit-account-loading">
        جاري تحميل بيانات المستخدم...
      </div>
    </div>
  );
}

if (accountLoadError) {
  return (
    <div
      className="edit-account-page"
      dir="rtl"
    >
      <div className="edit-account-error-card">
        <h2>
          تعذر فتح المستخدم
        </h2>

        <p>
          {accountLoadError}
        </p>

        <button
          type="button"
          className="edit-account-primary-btn"
          onClick={() =>
            navigate("/Account")
          }
        >
          العودة إلى الحسابات
        </button>
      </div>
    </div>
  );
}

if (!Account) {
  return null;
}

  const handleAddExtraImages = async () => {
    if (extraImages.length === 0) return;

    try {
      for (const image of extraImages) {
        const imageRef = ref(storage, `Account-images/${id}/${image.name}`);
        await uploadBytes(imageRef, image);
        const ImageUrl = await getDownloadURL(imageRef);

       await usersApi.addImage(
  id!,
  {
    imageUrl: ImageUrl,
  },
);
      }

      toast.success("✅ تم تحديث بيانات الصور الاضافية بنجاح!", {
        duration: 3000,
        position: "top-right",
        style: {
          background:
            "linear-gradient(to top, rgb(25, 113, 132), rgb(38, 155, 180))",
          color: "#fff",
          fontSize: "16px",
        },
      });
      setExtraImages([]);
      fetchAccount(); // إعادة تحميل الصور الإضافية
    } catch (error) {
      console.error("❌ خطأ في إضافة الصور الإضافية:", error);
    }
  };
  // ❌ دالة حذف الرقم التسلسلي

  // ✅ جلب البيانات عند تحميل الصفحة



const confirmDeleteImage = async () => {
  if (!imageToDelete || !id) {
    return;
  }

  /*
   * صورة إضافية محفوظة عبر Backend API
   */
  if (isUserWorkImage(imageToDelete)) {
    try {
      await usersApi.deleteImage(
        id,
        imageToDelete.imageId,
      );

      const storageRef = ref(
        getStorage(),
        imageToDelete.imageUrl,
      );

      await deleteObject(
        storageRef,
      ).catch(() => {
        console.warn(
          "تعذر حذف الصورة من Firebase Storage",
        );
      });

      setStoredExtraImages(
        (previous) =>
          previous.filter(
            (image) =>
              image.imageId !==
              imageToDelete.imageId,
          ),
      );

      toast.success(
        "تم حذف الصورة الإضافية بنجاح",
      );
    } catch (error) {
      console.error(
        "Error deleting user image:",
        error,
      );

      if (error instanceof ApiError) {
        if (error.status === 403) {
          toast.error(
            "لا تمتلك صلاحية حذف صور المستخدم",
          );

          return;
        }

        if (error.status === 404) {
          toast.error(
            "الصورة غير موجودة",
          );

          return;
        }

        toast.error(
          error.message ||
            "حدث خطأ أثناء حذف الصورة",
        );

        return;
      }

      toast.error(
        "تعذر حذف الصورة",
      );
    } finally {
      setImageToDelete(null);
    }

    return;
  }

  /*
   * صورة جديدة من الجهاز لم يتم رفعها بعد
   */
  if (imageToDelete instanceof File) {
    setExtraImages(
      (previous) =>
        previous.filter(
          (file) =>
            file !== imageToDelete,
        ),
    );

    setImageToDelete(null);

    return;
  }

  /*
   * string = الصورة الرئيسية
   */
  if (typeof imageToDelete === "string") {
    try {
      const storageRef = ref(
        getStorage(),
        imageToDelete,
      );

      await deleteObject(
        storageRef,
      ).catch(() => {
        console.warn(
          "تعذر حذف الصورة الرئيسية من Firebase Storage",
        );
      });

      await usersApi.update(
        id,
        {
          profileImageUrl: null,
        },
      );

      setMainImageUrl(null);

      setAccount(
        (previous) => {
          if (!previous) {
            return previous;
          }

          return {
            ...previous,
            profileImageUrl: null,
          };
        },
      );

      toast.success(
        "تم حذف الصورة الرئيسية بنجاح",
      );
    } catch (error) {
      console.error(
        "Error deleting profile image:",
        error,
      );

      if (error instanceof ApiError) {
        if (error.status === 403) {
          toast.error(
            "لا تمتلك صلاحية تعديل المستخدم",
          );

          return;
        }

        toast.error(
          error.message ||
            "حدث خطأ أثناء حذف الصورة الرئيسية",
        );

        return;
      }

      toast.error(
        "تعذر حذف الصورة الرئيسية",
      );
    } finally {
      setImageToDelete(null);
    }
  }
};

  return (
  <div className="edit-account-page">
    <div className="edit-account-header">
      <div>
        <span className="edit-account-badge">تعديل بيانات المستخدم</span>
        <h1 className="edit-account-title">
          تعديل بيانات {Account.accountType}
        </h1>
        <p className="edit-account-subtitle">
          إدارة بيانات الحساب، الصور، المواقع، ونقاط المستخدم من واجهة واحدة.
        </p>
      </div>

      <button
        type="button"
        className="edit-account-back-btn"
        onClick={() => navigate(-1)}
        title="رجوع"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
      </button>
    </div>

    <form onSubmit={handleSubmit} className="edit-account-form-card">
        {/* الحقول الأساسية */}
        <div>
          <label className="block text-gray-600 font-semibold mb-1 font-custom">
            الأســــــــــم:
          </label>
          <input
            type="text"
            value={Account.fullName}
            onChange={(e) =>
              setAccount({ ...Account, fullName: e.target.value })
            }
            required
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 font-custom"
          />
        </div>
        {/* <div>
          <label className="block text-gray-600 font-semibold mb-1 font-custom">
            نــــــــوع الـــحـــســـاب :
          </label>
          <input
            type="text"
            value={Account.accountType}
            onChange={(e) =>
              setAccount({ ...Account, accountType: e.target.value })
            }
            required
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 font-custom"
          />
        </div> */}
        <div>
          <label className="block text-gray-600 font-semibold mb-1 font-custom">
            رقم الهاتف:
          </label>
          <input
            type="text"
           value={Account.phoneNumber || ""}
            onChange={(e) =>
              setAccount({ ...Account, phoneNumber: e.target.value })
            }
            required
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 font-custom"
          />
        </div>

        <div>
          <label className="block text-gray-600 font-semibold mb-1 font-custom">
            عدد النقاط :
          </label>
         <input
  type="text"
  value={Account.points}
  readOnly
  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 font-custom"
/>
        </div>

        <div>
          <label className="block text-gray-600 font-semibold mb-1 font-custom">
            التقييم:
          </label>
        <input
  type="text"
  value={Account.rating || ""}
  readOnly
  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 font-custom"
/>
        </div>

        <div>
          <label className="block text-gray-600 font-semibold mb-1 font-custom">
            تاريخ الولادة:
          </label>
          <input
  type="text"
  value={Account.dateOfBirth || ""}
  readOnly
  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 font-custom"
/>
        </div>
        <div>
          <label className="block text-gray-600 font-semibold mb-1 font-custom">
            البريد الالكتروني:
          </label>
         <input
  type="email"
  value={Account.email || ""}
  onChange={(e) =>
    setAccount({
      ...Account,
      email: e.target.value,
    })
  }
  required
  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 font-custom"
/>
        </div>

        <div className="col-span-2">
          <label className="block text-gray-600 font-semibold mb-1 font-custom">
            الوصف:
          </label>
        <textarea
  value={Account.about || ""}
  readOnly
  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 font-custom"
/>
        </div>

        {/* ✅ عرض الصورة الرئيسية */}

  <div className="edit-account-form-actions">
  <button
    type="submit"
    disabled={loading}
    className="edit-account-primary-btn"
  >
    {loading ? "جاري التحديث..." : "تحديث البيانات الأساسية"}
  </button>
</div>
      </form>

<div className="edit-account-divider"></div>
   <div className="edit-account-tabs-card">
  <div className="edit-account-tabs">
      <button
  type="button"
  className={`edit-account-tab-btn ${
    activeTab === "images" ? "active" : ""
  }`}
  onClick={() => setActiveTab("images")}
>
  صور المستخدم
</button>

<button
  type="button"
  className={`edit-account-tab-btn ${
    activeTab === "locations" ? "active" : ""
  }`}
  onClick={() => setActiveTab("locations")}
>
  مواقع المستخدم
</button>

<button
  type="button"
  className={`edit-account-tab-btn ${
    activeTab === "Points" ? "active" : ""
  }`}
  onClick={() => setActiveTab("Points")}
>
  نقاط المستخدم
</button>
        </div>

        {/* محتوى التاب */}
<div className="edit-account-tab-content">
            {activeTab === "images" && (
            <div>
              {/* 🔽 هنا ضع كود صور المستخدم */}
              <div className="col-span-2 m-4 flex justify-center items-center p-4">
                <label className="text-black font-semibold text-lg font-custom text-center">
                  الصور الخاصة بالمستخدم
                </label>
              </div>

              <div className="col-span-2 m-4">
                <label className="block text-gray-700 font-semibold mb-2 text-lg font-custom">
                  الصورة المستخدم:
                </label>
            <div className="flex flex-wrap items-center gap-6 p-4 rounded-lg border">
  {/* عرض الصورة الحالية */}
  {mainImageUrl ? (
    <img
      src={mainImageUrl}
      alt="صورة المستخدم"
      className="w-24 h-24 object-cover rounded-lg border-2 border-gray-300 shadow-lg cursor-pointer"
      onClick={() => {
        setPreviewImageUrl(mainImageUrl);
        setShowImagePreviewModal(true);
        setImageToDelete(mainImageUrl);
       
        setConfirmDeleteVisible(false);
      }}
    />
  ) : (
    <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-500 font-custom">
      لا توجد صورة
    </div>
  )}

  {/* معاينة الصورة الجديدة قبل الرفع */}
  {mainImage && (
    <img
      src={URL.createObjectURL(mainImage)}
      alt="الصورة الجديدة"
      className="w-24 h-24 object-cover rounded-lg border-2 border-green-500 shadow-lg"
    />
  )}

  {/* اختيار صورة جديدة */}
  <label className="cursor-pointer bg-[#262d34] text-white px-6 py-3 rounded-lg shadow hover:bg-cyan-700 transition font-custom">
    اختيار صورة جديدة
    <input
      type="file"
      accept="image/*"
      className="hidden"
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) {
          setMainImage(file);
        }
      }}
    />
  </label>

  {/* زر التحديث يظهر فقط بعد اختيار صورة */}
  {mainImage && (
    <button
      type="button"
      onClick={handleUpdateMainImage}
      disabled={loading}
      className="bg-green-600 text-white px-6 py-3 rounded-lg font-custom hover:bg-green-700 transition disabled:bg-gray-400"
    >
      {loading ? "جاري التحديث..." : "تحديث الصورة"}
    </button>
  )}
</div>
              </div>

              {/* ✅ عرض الصور الإضافية */}
              <div className="col-span-2 m-4">
                <label className="block text-gray-700 font-semibold mb-2 text-lg font-custom">
                  الصور الإضافية:
                </label>
                {/* ✅ عرض الصور (القديمة والجديدة معًا) */}
                <div className="flex flex-wrap gap-3 p-3 bg-gray-100 rounded-lg shadow">
                  {/* ✅ عرض الصور المخزنة في Firestore */}
                {storedExtraImages.map(
  (image, index) => (
    <img
      key={image.imageId}
      src={image.imageUrl}
      alt={`صورة ${index + 1}`}
      className="w-20 h-20 object-cover rounded-lg border border-gray-300 shadow-lg cursor-pointer"
      onClick={() => {
        setPreviewImageUrl(
          image.imageUrl,
        );

        setImageToDelete(
          image,
        );

        setShowImagePreviewModal(
          true,
        );

        setConfirmDeleteVisible(
          false,
        );
      }}
    />
  ),
)}

                  {/* ✅ عرض الصور الجديدة التي تم اختيارها من الجهاز */}
                  {extraImages.map((file, index) => (
                    // <img
                    //   key={`new-${index}`}
                    //   src={URL.createObjectURL(file)}
                    //   alt={`صورة جديدة ${index + 1}`}
                    //   className="w-20 h-20 object-cover rounded-lg border-2 border-blue-500 shadow-lg cursor-pointer"
                    //   onClick={() => handleDeleteImageClick(file, false)}
                    // />
                    <img
                      key={`stored-${index}`}
                      src={URL.createObjectURL(file)}
                      alt={`صورة ${index + 1}`}
                      className="w-20 h-20 object-cover rounded-lg border border-gray-300 shadow-lg cursor-pointer"
                     onClick={() => {
  const previewUrl =
    URL.createObjectURL(file);

  setPreviewImageUrl(
    previewUrl,
  );

  setShowImagePreviewModal(
    true,
  );

  setImageToDelete(
    file,
  );

  setConfirmDeleteVisible(
    false,
  );
}}
                    />
                  ))}

                  {/* ✅ عرض الصور الجديدة التي تم اختيارها من الجهاز */}
                </div>
                <div className="mt-2 flex items-center gap-3">
                  {/* ✅ زر اختيار صور جديدة */}
                  <label className="mt-2 flex items-center justify-center cursor-pointer bg-[#262d34] text-white px-6 py-3 text-lg rounded-lg shadow hover:bg-[#2d1d34] transition font-custom w-full">
                    <span>إضافة صور جديدة</span>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) =>
                        setExtraImages([
                          ...extraImages,
                          ...Array.from(e.target.files || []),
                        ])
                      }
                      className="hidden"
                    />
                  </label>

                  {/* ✅ زر رفع الصور */}
                  {extraImages.length > 0 && (
                    <button
                      onClick={handleAddExtraImages}
                      className="mt-2 flex items-center justify-center bg-[#262d34] cursor-pointer  text-white px-6 py-3 text-lg rounded-lg shadow hover:hover:bg-[#2d1d34]  transition font-custom w-full"
                    >
                      رفع الصور
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "locations" && (
            <GoogleMapLoader>
              <div className="col-span-2 px-4 mt-4">
                <h2 className="text-lg font-bold text-gray-800 mb-4 font-custom">
                  المواقع المرتبطة بالحساب:
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 {userLocations.length === 0 ? (
  <div className="col-span-full py-10 text-center font-custom text-gray-500">
    لا توجد مواقع مرتبطة بهذا المستخدم
  </div>
) : (
  userLocations.map(
    (location, index) => (
      <div
        key={`${location.lat}-${location.lng}-${index}`}
        className="bg-white p-4 rounded-xl shadow-lg border border-gray-200"
      >
        <div className="rounded-xl overflow-hidden mb-2">
          <GoogleMap
            mapContainerStyle={
              mapContainerStyle
            }
            center={{
              lat: location.lat,
              lng: location.lng,
            }}
            zoom={15}
          >
            <Marker
              position={{
                lat: location.lat,
                lng: location.lng,
              }}
            />
          </GoogleMap>
        </div>

        <div className="text-center">
          <p className="text-gray-800 font-custom text-right font-semibold">
            🏠 الموقع:{" "}
            {location.locationName ||
              "غير محدد"}
          </p>

          <p className="text-gray-700 text-right font-custom">
            🏙️ المدينة:{" "}
            {location.City ||
              "غير محددة"}
          </p>
        </div>
      </div>
    ),
  )
)}
                </div>
              </div>
            </GoogleMapLoader>
          )}

          {activeTab === "Points" && (
            <div>
              <h2 className="text-lg font-bold text-gray-800 mb-4 font-custom">
                جدول النقاط الخاصة ب الحساب:
              </h2>
              
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-300 rounded-lg">
                 
             <thead>
  <tr>
    <th>رقم العملية</th>
    <th>السيريال</th>
    <th>النوع</th>
    <th>النقاط</th>
    <th>الرصيد قبل</th>
    <th>الرصيد بعد</th>
    <th>المصدر</th>
    <th>الملاحظة</th>
    <th>الإجراء</th>
  </tr>
</thead>
               <tbody>
  {userPointsData.map(
  (entry) => (
    <tr
      key={
        entry.id
      }
    >
        <td>
           {entry.transactionId}
        </td>

<td>
  {getTransactionSerial(entry) || "-"}
</td>
        <td>
          {entry.type === "earn"
            ? "إضافة"
            : entry.type ===
                "deduct"
            ? "سحب"
            : entry.type ===
                "refund"
            ? "استرجاع"
            : entry.type ===
                "expire"
            ? "انتهاء"
            : "تعديل"}
        </td>

        <td>
          {entry.points > 0
            ? `+${entry.points}`
            : entry.points}
        </td>

        <td>
          {entry.balanceBefore}
        </td>

        <td>
          {entry.balanceAfter}
        </td>

        <td>
          {entry.source}
        </td>

        <td>
          {entry.note ||
            "لا توجد ملاحظة"}
        </td>

        <td>
          <button
            type="button"
            onClick={() =>
              handleReversePoint(
                entry,
              )
            }
            disabled={
              pointsActionLoading
            }
            className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 transition font-custom disabled:opacity-50"
          >
            عكس العملية
          </button>
        </td>
      </tr>
    ),
  )}
</tbody>
                </table>

                {pointsHasMore &&
  pointsNextCursor && (
    <div className="mt-4 flex justify-center">
      <button
        type="button"
        disabled={
          pointsLoading
        }
        onClick={() =>
          fetchUserPoints({
            append: true,
            cursor:
              pointsNextCursor,
          })
        }
        className="rounded-lg bg-[#262d34] px-5 py-2 text-white font-custom disabled:opacity-50"
      >
        {pointsLoading
          ? "جاري التحميل..."
          : "تحميل المزيد"}
      </button>
    </div>
  )}
              </div>
              <div className="h-[2px] bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300 my-4"></div>

              <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="block mb-1 text-sm font-semibold text-gray-700 font-custom  text-center align-middle">
                    نوع العملية
                  </label>
                  <select
                    value={operationType}
                    onChange={(e) => setOperationType(e.target.value)}
                    className="w-full p-2 border rounded-lg font-custom"
                  >
                    <option value="add">إضافة</option>
                    <option value="withdraw">سحب</option>
                  </select>
                </div>

                <div>
                  <label className="block mb-1 text-sm font-semibold text-gray-700 font-custom">
                    عدد النقاط
                  </label>
                  <input
                    type="number"
                    value={newPoints}
                    onChange={(e) => setNewPoints(e.target.value)}
                    placeholder="0"
                    className="w-full p-2 border rounded-lg font-custom"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm font-semibold text-gray-700 font-custom">
              سبب العملية
                  </label>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                 placeholder="سبب العملية"
                    className="w-full p-2 border rounded-lg font-custom"
                  />
                </div>

                <div className="md:col-span-4 mt-3">
                  <button
  type="button"
  onClick={
    handleAddPoints
  }
  disabled={
    pointsActionLoading
  }
  className="w-full md:w-auto bg-[#262d34] text-white px-6 py-3 text-sm rounded-lg shadow hover:bg-cyan-700 transition font-custom disabled:cursor-not-allowed disabled:opacity-50"
>
  {pointsActionLoading
    ? "جاري تنفيذ العملية..."
    : "تنفيذ العملية"}
</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ✅ مودال تأكيد الحذف */}

      {showImagePreviewModal && previewImageUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-3xl w-[90%] relative">
            {/* ✅ عرض الصورة بحجم مناسب للشاشة */}
            <div className="flex justify-center items-center mb-4">
              <img
                src={previewImageUrl}
                alt="Preview"
                className="max-h-[70vh] w-auto object-contain rounded-lg"
              />
            </div>

            {/* ✅ أزرار أسفل الصورة */}
            <div className="flex justify-end gap-4">
              {!confirmDeleteVisible ? (
                <>
                  <button
                    onClick={() => {
                      setShowImagePreviewModal(false);
                      setPreviewImageUrl(null);
                    }}
                    className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 font-custom"
                  >
                    إغلاق
                  </button>
                  <button
                    onClick={() => {
                      setConfirmDeleteVisible(true);
                    }}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 font-custom"
                  >
                    حذف الصورة
                  </button>
                </>
              ) : (
                <div className="w-full flex flex-col md:flex-row items-center justify-between gap-3">
                  <p className="text-red-700 text-sm font-custom text-center md:text-right">
                    هل أنت متأكد من أنك تريد حذف هذه الصورة؟
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        confirmDeleteImage();
                        setShowImagePreviewModal(false);
                        setConfirmDeleteVisible(false);
                      }}
                      className="bg-red-700 text-white px-4 py-2 rounded hover:bg-red-800 font-custom"
                    >
                      نعم، احذفها
                    </button>
                    <button
                      onClick={() => setConfirmDeleteVisible(false)}
                      className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-600 font-custom"
                    >
                      إلغاء
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="h-[2px] bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300 my-4"></div>
    </div>
  );
};

export default EditAccount;
